import {
  Button,
  Center,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Select,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useAuth } from "modules/providers/auth-provider";
import React, { memo, useCallback } from "react";
import colors from "app/colors";
import UserName from "modules/_shared/user-name";
import UserEditAvatarButton from "../drawers/components/user-profile/user-edit-avatar-button";
import EmailAddress from "modules/_shared/email-address";
import {
  BuildingIcon,
  CopyIcon,
  GearIcon,
  GroupsOutlineIcon,
  InfoIcon,
  PersonOutlineIcon,
} from "modules/icons";
import UserOnlineStatus from "../drawers/components/user-profile/user-online-status";
import { InviteType } from "gql/graphql";
import Toast from "modules/toasts/toast";
import UserAttributes from "../drawers/components/user-profile/user-attributes";
import DownloadDesktopApp from "../drawers/components/download-desktop-app";
import { isEmpty } from "lodash";
import EnterKeySettings from "modules/user-profile/enter-key-settings";
import OutOfOfficeSettings from "modules/user-profile/out-of-office-settings";
import MeDistributionGroups from "../drawers/components/user-profile/me-distribution-groups";
import MeToggleSmsButton from "../drawers/components/user-profile/me-toggle-sms-button";
import UserOutOfOfficeInfo from "modules/user-profile/user-out-of-office-info";
import useDisplayToast from "modules/hooks/use-display-toast";
import useInvite from "modules/hooks/use-invite";
import useUserPreference from "modules/hooks/use-user-preference";
import {
  getUserMention,
  getE164PhoneNumber,
  getInviteLink,
} from "modules/utils/Functions";
import ToggleHideEmailButton from "./toggle-hide-email-button";
import ToggleHidePhoneButton from "./toggle-hide-phone-button";
import { supportedTranslationLanguages } from "modules/utils/translation";

const MeUserProfile = () => {
  const { me, organization } = useAuth();
  const { displayToast } = useDisplayToast();
  const { createInviteAPI } = useInvite();

  const { stringValue: locale, upsertUserPreference: onLocaleChanged } =
    useUserPreference({
      userId: me?.id,
      name: "LOCALE",
    });

  const onCreateInviteClicked = useCallback(async () => {
    if (!organization) {
      return;
    }
    const { data: inviteData } = await createInviteAPI({
      variables: {
        input: {
          organizationId: organization.id,
          type: InviteType.Organization,
        },
      },
    });
    if (!inviteData?.createInvite) {
      return;
    }
    navigator.clipboard.writeText(
      getInviteLink(origin, inviteData.createInvite.id),
    );
    displayToast(() => (
      <Toast
        message="Invite Link Copied!"
        leftIcon={<CopyIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
  }, [createInviteAPI, displayToast, organization]);

  if (!me) return null;

  return (
    <DrawerContent>
      <DrawerCloseButton left={8} top={8} color={colors.gray30} size="lg" />
      <DrawerHeader pt={8}>
        <Center fontFamily="Outfit" fontSize={26} fontWeight={700}>
          My Profile
        </Center>
      </DrawerHeader>

      <DrawerBody>
        <Stack spacing={8} w="full">
          <HStack w="full" spacing={4}>
            <UserEditAvatarButton />
            <VStack w="full" alignItems="flex-start">
              <HStack w="full">
                <Text fontSize={26} fontWeight={700}>
                  <UserName userId={me.id} />
                </Text>
              </HStack>
              <UserOnlineStatus user={me} />
            </VStack>
          </HStack>
          <UserOutOfOfficeInfo user={me} />
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <BuildingIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                Synco Info
              </Text>
            </HStack>
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                Username
              </Text>
              <VStack w="full" alignItems="flex-start">
                <Text fontSize={14} color={colors.gray50} fontWeight={500}>
                  {getUserMention(me)}
                </Text>
              </VStack>
            </HStack>
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                Synco Email
              </Text>
              <EmailAddress id={me.id} fontSize="14" truncate />
            </HStack>{" "}
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                Invites
              </Text>
              <VStack w="full" alignItems="flex-start">
                <Text color={colors.gray50} fontWeight={400} fontSize={14}>
                  Invite team members to join Synco
                </Text>
                <Button
                  variant="link"
                  onClick={onCreateInviteClicked}
                  fontWeight={700}
                  fontSize={12}
                  color={colors.blue}
                >
                  <HStack spacing={1}>
                    <CopyIcon boxSize={3} fill={colors.blue} />
                    <Text>Copy Invite Link</Text>
                  </HStack>
                </Button>
              </VStack>
            </HStack>
          </VStack>
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <PersonOutlineIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                User Info
              </Text>
            </HStack>
            <UserAttributes user={me} />
          </VStack>
          <DownloadDesktopApp />
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <InfoIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                Contact Info
              </Text>
            </HStack>
            {!isEmpty(me.phone) && (
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.gray0}
                py={2}
              >
                <Text
                  minW={140}
                  fontWeight={700}
                  fontSize={14}
                  color={colors.blackSecondary}
                >
                  Mobile
                </Text>
                <Text fontSize={14} color={colors.gray50} fontWeight={500}>
                  {getE164PhoneNumber(me.phone)}
                </Text>
                <Spacer />
                <ToggleHidePhoneButton />
              </HStack>
            )}
            {!isEmpty(me.email) && (
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.gray0}
                py={2}
              >
                <Text
                  minW={140}
                  fontWeight={700}
                  fontSize={14}
                  color={colors.blackSecondary}
                >
                  Email
                </Text>
                <Text fontSize={14} color={colors.gray50} fontWeight={500}>
                  {me.email}
                </Text>
                <Spacer />
                <ToggleHideEmailButton />
              </HStack>
            )}
          </VStack>
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <GearIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                My Settings
              </Text>
            </HStack>
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                Language
              </Text>
              <Select
                placeholder="Select One"
                fontSize={14}
                defaultValue={locale}
                onChange={(e) => {
                  onLocaleChanged(e.target.value);
                }}
              >
                {supportedTranslationLanguages.map(([langCode, langName]) => (
                  <option key={langCode} value={langCode}>
                    {langName}
                  </option>
                ))}
              </Select>
            </HStack>
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                SMS Delivery
              </Text>
              <Text fontWeight={500} fontSize={14} color={colors.gray50}>
                Deliver messages via SMS
              </Text>
              <Spacer />
              <MeToggleSmsButton />
            </HStack>
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
              alignItems="flex-start"
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                Enter Key
              </Text>
              <EnterKeySettings />
            </HStack>
            <OutOfOfficeSettings />
          </VStack>
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <GroupsOutlineIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                Groups
              </Text>
            </HStack>
            <MeDistributionGroups />
          </VStack>
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(MeUserProfile);
